"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { Search, Droplets } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { filterCoverageNote, filterFitmentNote, type FilterFitmentEvidence } from "@/lib/filters/fitment-evidence"
import { FilterApplianceChoices } from "@/components/filters/filter-appliance-choices"
import type { FinderResult } from "@/lib/filters/finder"
import { FilterOptionCard } from "@/components/filters/filter-card"
import { logoFor } from "@/lib/filters/brand-logos"
import { getFilterHubSlugForBrand } from "@/lib/filter-hub-slugs"
import { FilterReminderForm } from "@/components/price-watch/filter-reminder-form"
import { moduleFlag } from "@/lib/pip/module-flags"
import type { ToolComponentData } from "@/lib/tool-adapter"
import { readDeepLinkParam } from "@/lib/tools/deep-link"

// Reminder signup paused (owner hotfix 2026-07-11): unset = dev-preview only,
// NEXT_PUBLIC_MODULE_FILTER_REMINDER=1 + redeploy relaunches.
const SHOW_FILTER_REMINDER = moduleFlag('FILTER_REMINDER')

// ---------- Types preserved from legacy ----------

interface FilterResult {
  fitmentEvidence?: FilterFitmentEvidence
  id: string
  title: string
  brand: string
  model: string
  seo_slug: string
  /** The filter's home, /filters/<slug> — supplied by the resolver (lib/parts/fpn roots). */
  path: string
  lowest_price_cents: number | null
  images: string[]
  type: string
  is_oem_match: boolean
  /** FRIDGE-1088 — the (FFN) Fridge.com Filter Number, shown on every answer card. */
  ffn: string | null
}

/** FRIDGE-723: a filter PART NUMBER query answers with the code's family. */
interface ModelResult {
  code: string
  brand: string
  name: string | null
  replaces: string[]
  replacedBy: string[]
  note: string | null
  hubPath: string | null
}

interface FridgeResult {
  id: string
  title: string
  brand: string
  model: string
  seo_slug: string
}

interface SearchResult {
  compatibilityCoverage?: FinderResult["compatibilityCoverage"]
  applianceMatch?: FinderResult["applianceMatch"]
  fridgeCandidates?: FinderResult["fridgeCandidates"]
  fridge: FridgeResult | null
  model?: ModelResult | null
  filters: FilterResult[]
  /** FRIDGE-1088 — brand logos for the answer cards (lib/filters/brand-logos), from the API. */
  brandLogos?: Record<string, string>
}

const META = {
  name: "Refrigerator Water Filter Finder",
  category: "Filters & water" as const,
}

const BRANDS = [
  { name: "Samsung", filters: "HAF-QIN, HAF-CIN" },
  { name: "Whirlpool", filters: "EveryDrop EDR1-4" },
  { name: "LG", filters: "LT1000P, LT700P" },
  { name: "GE", filters: "RPWFE, XWFE" },
  { name: "Frigidaire", filters: "ULTRAWF, WF3CB" },
  { name: "KitchenAid", filters: "Uses EveryDrop" },
] as const

// ---------- Page ----------

// Accept (and ignore) toolData so the existing page.tsx server component still
// renders without changes. All copy + state lives inline now.
export function FilterFinderPageClient(_props?: { toolData?: ToolComponentData }) {
  const [query, setQuery] = useState("")
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<SearchResult | null>(null)
  const [noResults, setNoResults] = useState(false)
  const [failed, setFailed] = useState(false)

  // The filter-finder widget deep-links here as ?q=<model number>. Prefill and
  // run once so the visitor never retypes what the widget already captured.
  const handleSearch = async (overrideTerm?: string) => {
    const raw = (overrideTerm ?? query).trim()
    if (!raw) return
    setLoading(true)
    setNoResults(false)
    setFailed(false)
    setResult(null)

    try {
      const res = await fetch(`/api/filters/search?q=${encodeURIComponent(raw)}`)
      if (!res.ok) throw new Error('Filter search unavailable')
      const data = await res.json()

      if (data.applianceMatch?.status === 'ambiguous') {
        setResult({ fridge: null, filters: [], applianceMatch: data.applianceMatch, fridgeCandidates: data.fridgeCandidates })
      } else if (data.mode === "filter-code" && data.model && data.filters.length > 0) {
        // FRIDGE-723: the visitor typed the code off the old filter, not a
        // fridge model — the resolver answered with that code's family.
        setResult({ fridge: null, model: data.model, filters: data.filters, brandLogos: data.brandLogos })
      } else if (data.fridge && data.filters.length > 0) {
        setResult({ fridge: data.fridge, filters: data.filters, brandLogos: data.brandLogos, applianceMatch: data.applianceMatch })
      } else if (data.fridge) {
        setResult({ fridge: data.fridge, filters: [], applianceMatch: data.applianceMatch })
        setNoResults(true)
      } else {
        setNoResults(true)
      }
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }

  // Deep-link search runs inside an async block owned by the effect. The rule
  // accepts setState there and rejects it in the effect body proper; the ?q=
  // behaviour is identical.
  useEffect(() => {
    const term = readDeepLinkParam(window.location.search, "q")
    if (!term) return
    void (async () => {
      setQuery(term)
      await handleSearch(term)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Search logic preserved verbatim from the legacy page, plus `overrideTerm`
  // so the ?q= deep link can search before React commits the state update.

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="What filter does"
      heroTitleItalic="your"
      heroTitlePost="fridge actually take?"
      heroSubtitle="Enter your refrigerator model or the number on your old filter. Find matching catalog options, then confirm fit and exact-model certification before choosing."
      heroMeta={["Model or filter number", "Check fit and certification"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "Model", label: "Or filter number", note: "Two lookup paths" },
            { value: "Claims", label: "Verify exact model", note: "Not certification proof" },
            { value: "OEM", label: "+ aftermarket", note: "Both shown" },
            { value: "$0", label: "To use", note: "No account needed" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={[
        {
          question: "Where do I find my fridge's model number?",
          answer:
            "Inside the fridge, on a label stuck to the side wall, ceiling, or behind a crisper drawer. Format is usually a brand prefix followed by 6–10 alphanumeric characters (e.g., RF23DB9700, WRF767SDHZ, LFXS26973S).",
        },
        {
          question: "Are aftermarket filters as safe as OEM?",
          answer:
            "Check the exact model, certifier and specific reduction claims in the certification directory and manufacturer performance sheet. A standard number or a listing claim alone does not establish the same performance, safety or fit as another filter.",
        },
        {
          question: "How often should I replace my filter?",
          answer:
            "Follow the instructions for the exact installed filter. Calendar intervals, gallon limits and reduced-flow guidance vary by model. The finder does not know your installation date or water usage and cannot tell you that replacement is due.",
        },
        {
          question: "Why does my OEM filter cost so much more?",
          answer:
            "Prices vary by filter, pack size and seller. Compare the price per filter, exact fit and verified reduction claims. A lower price does not by itself establish equivalent performance.",
        },
        {
          question: "What if you don't have my model?",
          answer:
            "Try entering just the model number with no extra characters. If still no match, we offer manufacturer filter-finder shortcuts for the six largest brands at the bottom of this page.",
        },
      ]}
      relatedTools={[
        {
          title: "Filter Cost Calculator",
          category: "Filters & water",
          description: "OEM vs aftermarket 5-year cost.",
          href: "/tools/refrigerator-water-filter-cost-calculator",
          time: "~30 sec",
        },
        {
          title: "Refrigerator Size Calculator",
          category: "Size & Capacity",
          description: "Cubic-foot range for your household.",
          href: "/tools/refrigerator-size-calculator",
          time: "~30 sec",
        },
        {
          title: "Energy Cost Calculator",
          category: "Energy & Cost",
          description: "Yearly cost to run any model.",
          href: "/tools/energy-cost-calculator",
          time: "~30 sec",
        },
        {
          title: "Lifespan Estimator",
          category: "Specialty",
          description: "Years left on your fridge.",
          href: "/tools/refrigerator-lifespan-calculator",
          time: "~45 sec",
        },
      ]}
      cta={{
        title: "Need a new fridge altogether?",
        body: "Browse French door, side-by-side, and counter-depth refrigerators with built-in water filtration — every type, sortable by spec.",
        buttonLabel: "Browse all refrigerators",
        buttonHref: "/best-refrigerators",
      }}
    >
      {/* ============ FINDER ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          {/* Search card */}
          <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900">
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                STEP 01
              </span>
              <div className="flex-1 h-[1px] bg-gray-300" />
            </div>
            <h2
              className="text-gray-900 font-black"
              style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
            >
              One model number.
              <br />
              Explore recorded filter matches.
            </h2>
            <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
              Find your model on the label inside the fridge — usually on the side wall or ceiling.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                aria-label="Refrigerator model number"
                placeholder="RF23DB9700, WRF767SDHZ, LFXS26973S…"
                className="tool-input-clean flex-1 px-4 py-4 text-[18px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                style={{ fontVariantNumeric: "tabular-nums" }}
              />
              <button
                type="button"
                onClick={() => handleSearch()}
                disabled={loading || !query.trim()}
                className="px-6 py-4 text-[14px] font-bold uppercase tracking-[0.1em] bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] rounded-lg transition-colors disabled:opacity-100 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                <Search className="w-4 h-4" aria-hidden="true" />
                {loading ? "Searching…" : "Find filter"}
              </button>
            </div>
          </div>

          {/* Results region — always in the DOM (hidden until a search resolves)
              so the role="status" live region exists before content changes.
              [hidden] is excluded from the parent's space-y-* selector, so the
              empty region adds no vertical space. */}
          <div
            role="status"
            aria-live="polite"
            className="space-y-6"
            hidden={!result && !noResults && !failed}
          >
          {failed && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 text-center">
              <p className="text-[14px] text-gray-700">
                Filter search is unavailable. Please try again.
              </p>
            </div>
          )}
          {result?.applianceMatch?.status === 'ambiguous' && (
            <FilterApplianceChoices candidates={result.fridgeCandidates ?? []} truncated={result.applianceMatch.candidatesTruncated} />
          )}
          {result?.applianceMatch?.status === 'candidate' && (
            <p className="text-sm text-gray-700">Candidate only: confirm the complete model on your appliance label before choosing any filter shown below.</p>
          )}
          {result?.fridge && <p className="text-sm text-gray-700">{filterCoverageNote(result.compatibilityCoverage)}</p>}
          {/* Results — matched fridge + recorded filters */}
          {result && result.fridge && (
            <div className="bg-white rounded-xl p-5 md:p-6 border border-gray-200">
              <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-500 mb-1">
                {result.applianceMatch?.status === 'candidate' ? 'Candidate refrigerator' : 'Matched refrigerator'}
              </div>
              <Link
                href={`/products/${result.fridge.seo_slug}`}
                className="text-[16px] font-bold text-[#0469f0] hover:underline"
              >
                {result.fridge.title}
              </Link>
            </div>
          )}

          {result && result.model && (
            <div className="bg-white rounded-xl p-5 md:p-6 border border-gray-200">
              <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-500 mb-1">
                Matched filter number
              </div>
              <p className="text-[16px] font-bold text-gray-900">
                {result.model.brand && <span className="text-[#0469f0]">{result.model.brand}{' '}</span>}
                <span className="font-mono">{result.model.code}</span>
                {result.model.name ? ` — ${result.model.name}` : ""}
              </p>
              {result.model.note && <p className="text-[12px] text-gray-600 mt-1">{result.model.note}</p>}
              {result.model.replaces.length > 0 && (
                <p className="text-[12px] text-gray-600 mt-1">
                  Replaces <span className="font-mono">{result.model.replaces.join(", ")}</span>
                </p>
              )}
              {result.model.replacedBy.length > 0 && (
                <p className="text-[12px] text-gray-600 mt-1">
                  Superseded by <span className="font-mono">{result.model.replacedBy.join(", ")}</span>
                </p>
              )}
              {result.model.hubPath && (
                <Link href={result.model.hubPath} className="inline-block mt-2 text-[14px] font-semibold text-[#0469f0] hover:underline">
                  Every {result.model.code} option, prices compared →
                </Link>
              )}
            </div>
          )}

          {result && result.filters.length > 0 && (
            <div>
              <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                <h3 className="text-[16px] font-black tracking-[-0.02em] text-gray-900 inline-flex items-center gap-2">
                  <Droplets className="w-5 h-5 text-[#0469f0]" aria-hidden="true" />
                  {result.applianceMatch?.status === 'candidate' ? 'Filters for the candidate model' : 'Filters for this model'}
                </h3>
                <span className="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold">
                  {result.filters.length} match{result.filters.length === 1 ? "" : "es"}
                </span>
              </div>
              {/* Semantic list; Tailwind preflight already resets ul/li list
                  styling, role="list" restores list semantics under that reset. */}
              <ul role="list" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {result.filters.map((filter) => (
                  <li key={filter.id} className="h-full">
                    {/* FRIDGE-1088 — the shared filter card (photo or designed
                        brand tile, brand logo, part number, FFN chip, the
                        genuine / compatible pill). The link is the resolver's
                        path, never a literal root. */}
                    <FilterOptionCard
                      f={{
                        href: filter.path,
                        title: filter.title,
                        brand: filter.brand,
                        model: filter.model,
                        ffn: filter.ffn ?? null,
                        cents: filter.lowest_price_cents,
                        image: filter.images?.[0] ?? null,
                        genuine: filter.is_oem_match,
                      }}
                      logo={logoFor(result.brandLogos ?? {}, filter.brand)}
                      showKind
                      evidenceNote={result.fridge ? filterFitmentNote(filter.fitmentEvidence) : undefined}
                    />
                  </li>
                ))}
              </ul>

              {/* P06: 6-month replacement reminder, prefilled from the top
                  match. Reminder emails are gated inert (decision #10:
                  logged, never sent). */}
              {SHOW_FILTER_REMINDER && (
                <div className="mt-4">
                  <FilterReminderForm
                    filterModel={result.filters[0].model}
                    fridgeModel={result.fridge?.model}
                  />
                </div>
              )}
            </div>
          )}

          {/* Fallback — fridge matched, filters unknown */}
          {result && noResults && result.fridge && result.filters.length === 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 text-center">
              <p className="text-[14px] text-gray-700 mb-3">
                {result.applianceMatch?.status === 'candidate' ? 'We found a candidate refrigerator model, but no displayable filter matches for it.' : 'We found your refrigerator, but no displayable filter matches for it.'}
              </p>
              <p className="text-[12px] text-gray-500 mb-3">
                An empty result does not mean no filter fits. Confirm the complete model and filter number with the manufacturer:
              </p>
              <ul role="list" className="flex flex-wrap justify-center gap-2">
                {BRANDS.map((b) => {
                  const slug = getFilterHubSlugForBrand(b.name)
                  return slug ? (
                    <li key={b.name}>
                      <Link
                        href={`/filters/${slug}`}
                        className="block text-xs text-[#0469f0] border border-[#0469f0]/20 px-3 py-1.5 rounded-full hover:bg-[#0469f0]/5"
                      >
                        {b.name} filters
                      </Link>
                    </li>
                  ) : null
                })}
              </ul>
            </div>
          )}

          {/* Fallback — no fridge match at all */}
          {noResults && !result?.fridge && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 text-center">
              <p className="text-[14px] text-gray-700 mb-3">
                No matching refrigerator found for &ldquo;{query}&rdquo;.
              </p>
              <p className="text-[12px] text-gray-500 mb-3">
                Try entering just the model number, or browse filters by brand:
              </p>
              <ul role="list" className="flex flex-wrap justify-center gap-2">
                {BRANDS.map((b) => {
                  const slug = getFilterHubSlugForBrand(b.name)
                  return slug ? (
                    <li key={b.name}>
                      <Link
                        href={`/filters/${slug}`}
                        className="block text-xs text-[#0469f0] border border-[#0469f0]/20 px-3 py-1.5 rounded-full hover:bg-[#0469f0]/5"
                      >
                        {b.name} filters
                      </Link>
                    </li>
                  ) : null
                })}
              </ul>
            </div>
          )}

          {/* P06: no-results branch — reminder form with empty filter prefill
              (the user types the filter model they already know). */}
          {SHOW_FILTER_REMINDER && noResults && <FilterReminderForm fridgeModel={result?.fridge?.model} />}
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Shop the source"
          title="French door fridges to compare"
          description="Models that go 6 months between changes — fewer filters, lower cost."
          collectionType="french-door"
          fallbackCollectionType="counter-depth"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                How the lookup works
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{
                  fontSize: "clamp(24px, 3vw, 36px)",
                  letterSpacing: "-0.03em",
                  lineHeight: 1.05,
                }}
              >
                Real fridges. Real filters. <em className="italic text-[#ff9951]">No guessing.</em>
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Refrigerator lookups use recorded compatibility links. Filter-number lookups match
                catalog codes and listing text. These matches do not independently verify certification
                or every performance claim; confirm the exact model with the manufacturer.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Match your fridge",
                  d: "We look for a matching refrigerator model or recognized filter number in the available catalog.",
                },
                {
                  n: "02",
                  t: "Find the filter family",
                  d: "Where recorded compatibility or a filter-code match exists, we show the related filter family. Missing evidence stays a missing match.",
                },
                {
                  n: "03",
                  t: "Review matching options",
                  d: "Review available OEM and aftermarket listings and displayed prices. Check the manufacturer fit list and exact certification claims before buying.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ BRAND BROWSE ============ */}
      <section className="bg-white py-4 md:py-6">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
            <h2
              className="text-gray-900 font-black"
              style={{
                fontSize: "clamp(20px, 2.4vw, 28px)",
                letterSpacing: "-0.025em",
                lineHeight: 1.1,
              }}
            >
              Or browse filters by brand
            </h2>
            <span className="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold">
              Skip the lookup
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {BRANDS.map((b, i) => {
              const slug = getFilterHubSlugForBrand(b.name)
              return slug ? (
                <Link
                  key={b.name}
                  href={`/filters/${slug}`}
                  className="group relative bg-white border border-gray-900 rounded-lg overflow-hidden hover:shadow-md transition-all"
                >
                  <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#ff9951] opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="px-4 py-5 flex flex-col items-start">
                    <span className="font-mono text-[10px] font-bold tracking-[0.15em] text-[#ff9951] mb-1">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="font-black text-[16px] text-gray-900 tracking-tight leading-tight">
                      {b.name}
                    </p>
                    <p className="text-[11px] text-gray-500 mt-1 font-mono leading-snug">
                      {b.filters}
                    </p>
                  </div>
                </Link>
              ) : null
            })}
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth fridges to compare"
          description="Explore counter-depth models and check the filtration specifications for each exact appliance."
          collectionType="counter-depth"
          fallbackCollectionType="side-by-side"
        />
      </section>
    </ToolPageShell>
  )
}
